const DEFAULT_API_URL = 'https://api.robrion.net';

export const API_URL = (import.meta.env.VITE_API_URL || DEFAULT_API_URL).replace(/\/+$/, '');

export interface ApiEnvelope<T> {
  success: boolean;
  data: T;
  meta?: { timestamp?: string; [key: string]: unknown };
  error?: { code?: string; message?: string };
}

export class AioApiError extends Error {
  constructor(message: string, public readonly status?: number) {
    super(message);
    this.name = 'AioApiError';
  }
}

let accessToken: string | undefined;

export const authToken = {
  get: () => accessToken,
  set: (token: string | undefined) => { accessToken = token; },
  clear: () => { accessToken = undefined; },
};

export async function apiRequest<T>(path: string, init: RequestInit = {}): Promise<ApiEnvelope<T>> {
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);

  let response: Response;
  try {
    response = await fetch(`${API_URL}${path}`, { ...init, headers, credentials: 'include' });
  } catch {
    throw new AioApiError('Unable to reach the RoBrion API. Check your connection and retry.');
  }

  let payload: ApiEnvelope<T>;
  try {
    payload = await response.json() as ApiEnvelope<T>;
  } catch {
    throw new AioApiError(`The API returned an invalid response (HTTP ${response.status}).`, response.status);
  }

  if (!response.ok || !payload.success) {
    if (response.status === 401) authToken.clear();
    throw new AioApiError(payload.error?.message || `Request failed (HTTP ${response.status}).`, response.status);
  }
  return payload;
}

export const api = {
  user: () => apiRequest<unknown>('/api/v1/user'),
  history: () => apiRequest<unknown[]>('/api/v1/history'),
  favorites: () => apiRequest<unknown[]>('/api/v1/favorites'),
  addFavorite: (toolId: string) => apiRequest('/api/v1/favorites', { method: 'POST', body: JSON.stringify({ toolId }) }),
  removeFavorite: (toolId: string) => apiRequest(`/api/v1/favorites/${encodeURIComponent(toolId)}`, { method: 'DELETE' }),
  recordHistory: (toolId: string, parameters: Record<string, unknown>) => apiRequest('/api/v1/history', { method: 'POST', body: JSON.stringify({ toolId, parameters }) }),
  execute: (toolId: string, parameters: Record<string, unknown>) => apiRequest<{ output: unknown }>(`/api/v1/tools/${encodeURIComponent(toolId)}`, { method: 'POST', body: JSON.stringify({ parameters }) }),
  health: () => apiRequest<unknown>('/health'),
};
